import React, { FC } from 'react';
import styled from 'styled-components';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
} from '@mui/material';
import { TruthTableEntry } from '../../../common/model/dto/lot-calibration-data';
import Variables from '../../../design-system/variables';
import Colours from '../../../design-system/colours';
import { useTranslation } from 'react-i18next';

interface OvCalibrationTruthTableProps {
  data: TruthTableEntry[];
  unit: string;
}

const OvCalibrationTruthTable: FC<OvCalibrationTruthTableProps> = ({
  data,
  unit,
}) => {
  const { t } = useTranslation();

  if (!data || data.length === 0) {
    return <EmptyMessage>{t('lots.calibration.noTruthTableData')}</EmptyMessage>;
  }

  return (
    <Container>
      <TableContainer component={Paper} elevation={0}>
        <StyledTable size="small">
          <TableHead>
            <TableRow>
              <HeaderCell>{t('lots.calibration.truthTable.signal')}</HeaderCell>
              <HeaderCell>
                {t('lots.calibration.truthTable.concentration')} ({unit})
              </HeaderCell>
              {data[0].signalMinusError !== undefined && (
                <>
                  <HeaderCell>
                    {t('lots.calibration.truthTable.signalMinusError')}
                  </HeaderCell>
                  <HeaderCell>
                    {t('lots.calibration.truthTable.concentrationMinusError')} ({unit})
                  </HeaderCell>
                </>
              )}
              {data[0].signalPlusError !== undefined && (
                <>
                  <HeaderCell>
                    {t('lots.calibration.truthTable.signalPlusError')}
                  </HeaderCell>
                  <HeaderCell>
                    {t('lots.calibration.truthTable.concentrationPlusError')} ({unit})
                  </HeaderCell>
                </>
              )}
            </TableRow>
          </TableHead>
          <TableBody>
            {data.map((entry, index) => (
              <TableRow key={index}>
                <Cell>{entry.signal}</Cell>
                <Cell>{entry.concentration.toFixed(2)}</Cell>
                {entry.signalMinusError !== undefined && (
                  <>
                    <Cell>{entry.signalMinusError}</Cell>
                    <Cell>{entry.concentrationMinusError?.toFixed(2) ?? '-'}</Cell>
                  </>
                )}
                {entry.signalPlusError !== undefined && (
                  <>
                    <Cell>{entry.signalPlusError}</Cell>
                    <Cell>{entry.concentrationPlusError?.toFixed(2) ?? '-'}</Cell>
                  </>
                )}
              </TableRow>
            ))}
          </TableBody>
        </StyledTable>
      </TableContainer>
    </Container>
  );
};

export default OvCalibrationTruthTable;

const Container = styled.div`
  margin-top: 1rem;
`;

const StyledTable = styled(Table)`
  && {
    border: 1px solid ${Colours.GRAY['200']};
    border-radius: ${Variables.borderRadius.SMALL};
  }
`;

const HeaderCell = styled(TableCell)`
  && {
    font-weight: bold;
    background-color: ${Colours.OV_LIGHT_GRAY};
    font-size: ${Variables.fontSizes.SMALL};
    padding: 0.75rem;
  }
`;

const Cell = styled(TableCell)`
  && {
    font-size: ${Variables.fontSizes.SMALL};
    padding: 0.75rem;
  }
`;

const EmptyMessage = styled.div`
  padding: 1rem;
  text-align: center;
  color: ${Colours.GRAY['500']};
`;

